<template>
  <template v-if="computedBlueprint?.length">
    <section
      v-for="c in computedBlueprint"
      :id="c?.fields?.identifier?.toString?.()"
      :key="c?.sys?.id"
      :ref="c?.sys?.id"
      :data-type="c?.fields?.renderAs || c?.sys?.contentType?.sys?.id"
      :data-entry-id="c?.sys?.id"
      :data-entry-title="c?.fields?.entryTitle?.toString?.() || undefined"
      :style="c?.fields?.cssOverride as StyleValue"
      :class="[isInspectorMode ? 'inspector-mode-active' : '']"
    >
      <div v-if="isInspectorMode" class="blueprint-inspector">
        <div class="blueprint-inspector-badge">
          <span class="badge-type">
            <template
              v-if="
                c?.fields?.renderAs &&
                c?.fields?.renderAs !== c?.sys?.contentType?.sys?.id
              "
            >
              <span class="badge-base-type">{{
                c?.sys?.contentType?.sys?.id
              }}</span>
              <IconChevronRight width="10" height="10" class="badge-arrow" />
              <span>{{ c?.fields?.renderAs }}</span>
            </template>
            <template v-else>
              {{ c?.fields?.renderAs || c?.sys?.contentType?.sys?.id }}
            </template>
          </span>
          <a
            v-if="c?.sys?.id"
            :href="`https://app.contentful.com/spaces/0piqveu8x9oj/entries/${c?.sys?.id}`"
            target="_blank"
            class="badge-link"
            title="Open Contentful Entry"
          >
            {{ c?.sys?.id }}
            <IconExternalLink width="12" height="12" />
          </a>
          <EntryStatus v-if="c?.sys" :status="getEntryStatus(c?.sys)" />
        </div>
      </div>
      <component
        :is="
          computedComponents[
            c?.fields?.renderAs?.toString?.() ||
              c?.sys?.contentType?.sys?.id?.toString?.() ||
              ''
          ]
        "
        :data="c"
      />
    </section>
  </template>
</template>

<script setup lang="ts">
import ArticleCards from '~/components/ArticleCards.vue'
import ArticleListing from '~/components/ArticleListing.vue'
import ArticleSearch from '~/components/ArticleSearch.vue'
import BenefitsBlock from '~/components/BenefitsBlock.vue'
import CardsBlock from '~/components/CardsBlock.vue'
import ContentBlock from '~/components/ContentBlock.vue'
import DistributorsList from '~/components/DistributorsList.vue'
import DocumentationDownload from '~/components/DocumentationDownload.vue'
import DownloadableGroup from '~/components/DownloadableGroup.vue'
import EntriesCollection from '~/components/EntriesCollection.vue'
import EventCards from '~/components/EventCards.vue'
import EventListing from '~/components/EventListing.vue'
import EventsCalendar from '~/components/EventsCalendar.vue'
import FormatsOverview from '~/components/FormatsOverview.vue'
import GrowBusiness from '~/components/GrowBusiness.vue'
import HeroBlock from '~/components/HeroBlock.vue'
import HeroBlocks from '~/components/HeroBlocks.vue'
import HeroBlocksArticles from '~/components/HeroBlocksArticles.vue'
import HighlightsGrid from '~/components/HighlightsGrid.vue'
import ImageGallery from '~/components/ImageGallery.vue'
import InformationBlock from '~/components/InformationBlock.vue'
import LongFormEditorial from '~/components/LongFormEditorial.vue'
import LoginBlock from '~/components/LoginBlock.vue'
import JoinWPN from '~/components/JoinWPN.vue'
import MarketingMaterialSet from '~/components/MarketingMaterialSet.vue'
import MediaListing from '~/components/MediaListing.vue'
import MetricsDashboard from '~/components/MetricsDashboard.vue'
import NewsCarousel from '~/components/NewsCarousel.vue'
import NewsletterSignUp from '~/components/NewsletterSignUp.vue'
import PodcastsAndVideos from '~/components/PodcastsAndVideos.vue'
import PreferredPartners from '~/components/PreferredPartners.vue'
import ProductSet from '~/components/ProductSet.vue'
import ProductsGrid from '~/components/ProductsGrid.vue'
import ProductsPromo from '~/components/ProductsPromo.vue'
import ProductTimeline from '~/components/ProductTimeline.vue'
import ProfileHero from '~/components/ProfileHero.vue'
import ProfileMetrics from '~/components/ProfileMetrics.vue'
import PromoBanner from '~/components/PromoBanner.vue'
import ResourceHub from '~/components/ResourceHub.vue'
import SearchResults from '~/components/SearchResults.vue'
import SquareConnect from '~/components/SquareConnect.vue'
import StoreInfo from '~/components/StoreInfo.vue'
import SpecialFeatures from '~/components/SpecialFeatures.vue'
import TierBenefits from '~/components/TierBenefits.vue'
import UserBenefits from '~/components/UserBenefits.vue'
import WPNExplainer from '~/components/WPNExplainer.vue'
import HotspotsMap from '~/components/HotspotsMap.vue'
import FlexibleContentPanels from '~/components/FlexibleContentPanels.vue'
import CuratedArticlesCarousel from '~/components/CuratedArticlesCarousel.vue'
import DynamicArticlesCarousel from '~/components/DynamicArticlesCarousel.vue'
import SlimBanner from '~/components/SlimBanner.vue'

import type { TypePageBuilderWithoutUnresolvableLinksResponse } from '~/types/contentful'
import type { Component, StyleValue } from 'vue'
import { getEntryStatus } from '~/utils/contentfulStatus'

const { $visibility } = useNuxtApp()
const globalStore = useGlobalStore()

const isInspectorMode = computed(() => globalStore.isInspectorMode)

const props = defineProps<{
  data: TypePageBuilderWithoutUnresolvableLinksResponse['fields']['blueprint']
}>()

const computedBlueprint = computed(() => {
  return props.data?.filter((entry) => {
    return entry?.fields?.visibility &&
      instanceOfVisiblity(entry?.fields?.visibility)
      ? $visibility(entry?.fields?.visibility)
      : true
  })
})

const computedComponents = computed<Record<string, Component>>(() => {
  return {
    // Render As - Component name starts with an uppercase letter
    ArticleCards,
    ArticleSearch,
    BenefitsBlock,
    CardsBlock,
    GrowBusiness,
    // ArticleSet Render As aliases
    CuratedArticlesCarousel,
    DynamicArticlesCarousel,
    // CopySideBlock
    DistributorsList,
    DocumentationDownload,
    DownloadableGroup,
    EventCards,
    EventListing,
    EventsCalendar,
    // FormatsBlock
    HeroBlocks,
    HeroBlocksArticles,
    HighlightsGrid,
    InformationBlock,
    JoinWPN,
    LoginBlock,
    NewsCarousel,
    NewsletterSignUp,
    // PageHeader
    PodcastsAndVideos,
    ProductSet,
    ProfileHero,
    PromoBanner,
    SlimBanner,
    SquareConnect,
    StoreInfo,
    UserBenefits,
    WPNExplainer,

    // Content Type - Component name starts with a lowercase letter
    articleListing: ArticleListing,
    articleSet: CuratedArticlesCarousel,
    contentBlock: ContentBlock,
    entriesCollection: EntriesCollection,
    formatsOverview: FormatsOverview,
    heroBlock: HeroBlock,
    imageGallery: ImageGallery,
    longFormEditorial: LongFormEditorial,
    marketingMaterialSet: MarketingMaterialSet,
    mediaListing: MediaListing,
    metricsDashboard: MetricsDashboard,
    preferredPartners: PreferredPartners,
    productsGrid: ProductsGrid,
    productsPromo: ProductsPromo,
    productSet: ProductSet,
    productTimeline: ProductTimeline,
    profileMetrics: ProfileMetrics,
    resourceHub: ResourceHub,
    searchResults: SearchResults,
    specialFeatures: SpecialFeatures,
    tierBenefits: TierBenefits,
    hotspotsMap: HotspotsMap,
    flexibleContentPanels: FlexibleContentPanels,
  }
})
</script>

<style scoped>
.inspector-mode-active {
  position: relative;
  outline: 2px dashed var(--purple-light);
  outline-offset: -2px;
}

.blueprint-inspector {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  padding: 4px;
  pointer-events: none;
}

.blueprint-inspector-badge {
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid var(--purple-light);
  border-radius: 6px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--base-font-family);
  font-size: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.blueprint-inspector-badge:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.badge-type {
  font-weight: 700;
  color: var(--purple-dark);
  text-transform: capitalize;
  display: flex;
  align-items: center;
  gap: 6px;
}

.badge-base-type {
  font-weight: 500;
  color: var(--gray-7);
  font-size: 11px;
}

.badge-arrow {
  color: var(--gray-5);
}

.badge-link {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--purple);
  text-decoration: none;
  font-family: monospace;
  font-size: 11px;
  background: rgba(80, 78, 139, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.badge-link:hover {
  background: rgba(80, 78, 139, 0.2);
  color: var(--purple-dark);
}
</style>
